import { assertNotEmpty, type Optional } from '@festina-life/core'
import { Button } from '@festina-life/ui/actions'
import { RawSelect } from '@festina-life/ui/forms'
import { useTranslate } from '@festina-life/ui/hooks'
import { useEffect, useState } from 'react'

import { useTemplateContext } from '../../context/templateContext'
import type { TemplateReferenceValueSetupField } from '../../types/templateModel'
import {
  generateEmptyTemplateValueSetup,
  generateEmptyTemplateVariant,
} from '../../uiModels/createEmptyFieldHelpers'
import { useUnusedMappingKeys } from './useUnusedMappingKeys'

type Props = {
  field: TemplateReferenceValueSetupField
}

/**
 * Row used for adding a new template reference to template value setup field.
 * The component is loaded in edit mode by the details panel to load the list of
 * available templates and allow the user to add new template references to the value
 * setup.
 */
export const TemplateReferenceAddMappingRow = ({ field }: Props) => {
  const { templateVersion } = useTemplateContext()
  const translate = useTranslate()
  const unusedKeyOptions = useUnusedMappingKeys(field)
  const [currentKey, setCurrentKey] = useState<Optional<string>>(
    unusedKeyOptions[0],
  )

  useEffect(() => {
    if (currentKey && !unusedKeyOptions.includes(currentKey)) {
      setCurrentKey(unusedKeyOptions[0])
    }
  }, [currentKey, unusedKeyOptions])

  assertNotEmpty(templateVersion)

  const handleValueChanged = (nextValue: Optional<string>) => {
    setCurrentKey(nextValue)
  }

  const handleAddVariant = async () => {
    if (currentKey) {
      const fieldValue = field.value ?? generateEmptyTemplateValueSetup(field)
      assertNotEmpty(templateVersion)

      const nextVariant = generateEmptyTemplateVariant({
        key: currentKey,
        fieldValue,
      })
      await field.update({
        ...fieldValue,
        origin: fieldValue.path,
        variants: [nextVariant, ...fieldValue.variants],
      })
    }
  }

  return (
    <div className="flex flex-row items-center gap-x-2">
      <RawSelect
        options={unusedKeyOptions}
        onChange={handleValueChanged}
        value={currentKey}
      />
      <Button onClick={handleAddVariant} disabled={!currentKey}>
        {translate('add')}
      </Button>
    </div>
  )
}
